import { cond, getMaybeUndefined } from "@services/sdk/helpers";
import { UsageEvent } from "@services/sdk/types";
import moment from "moment";

export interface IUsageTotalConstructorParams {
	unit: string;
	type: string;
	value: number;
}

class UsageTotal {
	private static GetType =
		(type: UsageEvent) =>
		(arr: Array<UsageTotal>): UsageTotal => {
			if (!arr) {
				return UsageTotal.ConstructEmpty(type);
			}

			const res = arr.find((usageTotal) => usageTotal.type === type);

			if (typeof res === "undefined") {
				return UsageTotal.ConstructEmpty(type);
			}

			return res;
		};

	public static GetData = UsageTotal.GetType(UsageEvent.DATA);

	public static GetCalls = UsageTotal.GetType(UsageEvent.CALLS);

	public static GetText = UsageTotal.GetType(UsageEvent.SMS);

	public static ConstructList(
		arrParams: Array<IUsageTotalConstructorParams>,
	): Array<UsageTotal> {
		return arrParams.map(UsageTotal.Construct);
	}

	public static EmptyConstructorParams: IUsageTotalConstructorParams = {
		type: "",
		unit: "",
		value: 0,
	};

	public static ConstructEmpty(type: UsageEvent): UsageTotal {
		return cond(
			[
				() => type === UsageEvent.DATA,
				UsageTotal.Construct({ type: "data", unit: "kb", value: 0 }),
			],
			[
				() => type === UsageEvent.CALLS,
				UsageTotal.Construct({
					type: "calls",
					unit: "minutes",
					value: 0,
				}),
			],
			[
				() => type === UsageEvent.SMS,
				UsageTotal.Construct({
					type: "messages",
					unit: "number",
					value: 0,
				}),
			],
			[() => true, UsageTotal.Construct(UsageTotal.EmptyConstructorParams)],
		);
	}

	public static Construct(params: IUsageTotalConstructorParams): UsageTotal {
		return new UsageTotal(params);
	}

	responseData: IUsageTotalConstructorParams;

	constructor(params: IUsageTotalConstructorParams) {
		this.responseData = params;
	}

	get type(): UsageEvent | null {
		return cond(
			[() => this.responseData.type === "data", UsageEvent.DATA],
			[() => this.responseData.type === "calls", UsageEvent.CALLS],
			[() => this.responseData.type === "messages", UsageEvent.SMS],
			[() => true, null],
		);
	}

	get rawValue(): number {
		return this.responseData.value;
	}

	get label(): string {
		const typeMap = new Map<UsageEvent, string>([
			[UsageEvent.DATA, "Data"],
			[UsageEvent.CALLS, "Calls"],
			[UsageEvent.SMS, "Text"],
		]);

		if (!this.type) {
			return "";
		}

		return getMaybeUndefined(typeMap.get(this.type), "");
	}

	get value(): string {
		if (!this.type) {
			return "0";
		}

		const typeMap = new Map<UsageEvent, (usageTotal: UsageTotal) => string>([
			[
				UsageEvent.DATA,
				(usageTotal) => {
					const val = (usageTotal.rawValue / (1024 * 1024)).toFixed(2);

					return `${val} GB`;
				},
			],
			[
				UsageEvent.CALLS,
				(usageTotal) => {
					const duration = moment.duration(usageTotal.rawValue * 1000);

					return moment.utc(duration.as("milliseconds")).format("HH:mm:ss");
				},
			],
			[
				UsageEvent.SMS,
				(usageTotal) => {
					return `${usageTotal.rawValue} SMS`;
				},
			],
		]);

		const fn: Function = getMaybeUndefined(typeMap.get(this.type), () => "");

		return fn(this);
	}

	public getPercentageChange(prevUsageTotal: UsageTotal): number {
		if (this.isEmpty(prevUsageTotal)) {
			return 0;
		}

		const curVal = this.rawValue;
		const prevVal = prevUsageTotal.rawValue;

		return Math.round(((curVal - prevVal) / prevVal) * 100);
	}

	public isEmpty(prevUsageTotal: UsageTotal): boolean {
		return prevUsageTotal.rawValue === 0;
	}
}

export default UsageTotal;
